import { createFileRoute } from '@tanstack/react-router';
import { requireSession } from '@/lib/route-guards';
import { warm, warmBackground } from '@/api/loaders';
import { eventsList, productionsList, venuesList } from '@/api/resources';
import { EVENTS_SCREEN_DEFAULT_RANGE, EVENT_RANGES, eventsRangeQuery } from '@/lib/event-ranges';
import type { EventRange } from '@/lib/event-ranges';
import { searchEnum } from '@/lib/search-enum';
import { useSetViewShim } from '@/lib/use-set-view';
import { EventsView } from '@/screens/events';

// The window the table is showing. In the URL rather than in a `useState`
// because the loader must warm the slot the screen will actually subscribe to,
// and component state does not exist when the loader runs.
const eventWindow = searchEnum('window', EVENT_RANGES, EVENTS_SCREEN_DEFAULT_RANGE);

export const Route = createFileRoute('/backend/events')({
  validateSearch: (s: Record<string, unknown>) => ({
    focus: typeof s.focus === 'string' ? s.focus : undefined,
    window: eventWindow.parse(s),
  }),
  loaderDeps: ({ search }) => ({ window: search.window }),
  // Search-driven route: no skeleton for an in-screen navigation (router.ts).
  pendingMs: Infinity,
  beforeLoad: requireSession,
  // Block only on the rows this screen is ABOUT; warm its companions in the
  // background. Awaiting everything would trade one problem for another —
  // no empty flash, but a cold load that shows the previous route until the
  // slowest query lands. The table renders its own loading state for
  // whatever is still in flight.
  //
  // The window comes from `deps`, so this warms the exact slot the screen will
  // subscribe to — including when the operator arrives on a shared link with
  // `?window=past_week`. It used to warm `eventsList({})`: an unfiltered read of
  // the 1000 OLDEST performances that no screen subscribes to, blocked on,
  // discarded, and followed by a cold mount. That is worse than having no
  // loader, and it is the exact defect this whole layer exists to prevent.
  loader: ({ context, deps }) => {
    warmBackground(context.queryClient, productionsList({}), venuesList({}));
    return warm(context.queryClient, eventsList(eventsRangeQuery(deps.window)));
  },
  component: Page,
});

function Page() {
  const setView = useSetViewShim();
  const { focus, window } = Route.useSearch();
  const navigate = Route.useNavigate();
  return (
    <EventsView
      setView={setView}
      focus={focus}
      dateRange={window}
      onDateRangeChange={(next: EventRange) => void navigate({ search: (p) => ({ ...p, window: next }) })}
    />
  );
}
